import * as React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SectionHeading from './SectionHeading';
import Reveal from './motion/Reveal';

const faqs = [
  {
    id: 'panel1',
    q: 'How do you ensure project success?',
    a: 'We follow agile methodologies with continuous feedback and adaptability. Our team focuses on quality, performance, and security while keeping communication transparent. We also offer post-launch support and maintenance to ensure long-term success.',
  },
  {
    id: 'panel2',
    q: 'What industries do you serve?',
    a: 'Our expertise spans technology, fintech, healthcare, e-commerce, gaming, cybersecurity, and enterprise solutions. We work closely with clients to build solutions aligned with their specific industry challenges.',
  },
  {
    id: 'panel3',
    q: 'What services does your consultancy provide?',
    a: 'We specialize in end-to-end software development — cloud applications, mobile apps, desktop software, and game development. Our expertise also extends to architecture consulting, DevOps, system integration, and UI/UX design.',
  },
  {
    id: 'panel4',
    q: 'How do you approach new projects?',
    a: 'Our process begins with a thorough consultation and requirement analysis to understand your goals. We then provide a detailed roadmap — feasibility assessments, architecture planning, and proof-of-concept development before full-scale implementation.',
  },
  {
    id: 'panel5',
    q: 'Do you work with startups and enterprises alike?',
    a: 'Absolutely. For startups we focus on MVP development and scalability; for enterprises we provide enterprise-grade solutions, system modernization, and process automation.',
  },
];

export default function FAQ() {
  const [expanded, setExpanded] = React.useState('panel1');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Container id="faq" sx={{ pt: { xs: 6, sm: 12 }, pb: { xs: 8, sm: 16 } }}>
      <SectionHeading
        eyebrow="FAQ"
        title="Questions,"
        highlight="answered"
        subtitle="Everything you need to know about working with us. Can't find what you're looking for? Reach out any time."
        align="center"
        maxWidth={680}
      />

      <Box sx={{ maxWidth: 820, mx: 'auto', mt: { xs: 4, sm: 6 } }}>
        {faqs.map((faq, i) => {
          const isOpen = expanded === faq.id;
          return (
            <Reveal key={faq.id} delay={i * 0.05} y={18}>
              <Accordion
                expanded={isOpen}
                onChange={handleChange(faq.id)}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1.5,
                  borderRadius: '14px !important',
                  border: '1px solid',
                  borderColor: isOpen ? 'primary.main' : 'divider',
                  bgcolor: isOpen
                    ? (t) =>
                        t.palette.mode === 'dark'
                          ? 'hsla(210,98%,55%,0.05)'
                          : 'hsla(210,98%,55%,0.04)'
                    : 'background.paper',
                  overflow: 'hidden',
                  transition: 'border-color 0.25s ease, background-color 0.25s ease',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        color: isOpen ? '#fff' : 'text.secondary',
                        background: isOpen
                          ? 'linear-gradient(135deg, hsl(210,98%,52%), hsl(265,90%,60%))'
                          : 'transparent',
                        border: isOpen ? 'none' : '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.25s ease',
                      }}
                    >
                      <AddRoundedIcon fontSize="small" />
                    </Box>
                  }
                  sx={{
                    px: 3,
                    py: 1,
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                      transform: 'rotate(135deg)',
                    },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '1.02rem' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Reveal>
          );
        })}
      </Box>
    </Container>
  );
}
